"use client"

import React from 'react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'
import { UITypography } from '../display/ui-typography'
import { UIProgress } from '../display/ui-progress'
import { UIBadge } from '../display/ui-badge'
import { CheckCircle2, Circle, Loader2, XCircle, AlertCircle } from 'lucide-react'

export interface UITaskStep {
  id: string
  title: string
  description?: string
  status: 'pending' | 'running' | 'completed' | 'error' | 'skipped'
  progress?: number
}

export interface UITaskProps {
  // 핵심 데이터
  title: string
  description?: string
  steps: UITaskStep[]

  // 상태
  currentStep?: string
  overallProgress?: number

  // UI 옵션
  variant?: 'default' | 'minimal'
  showProgress?: boolean
  showDescription?: boolean
  showStepNumber?: boolean

  // 스타일링
  className?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 콜백
  onStepClick?: (stepId: string) => void
}

/**
 * UITask - 작업 진행 상태를 표시하는 컴포넌트
 * shadcn.io/ai Task 컴포넌트 기반으로 구현
 */
export const UITask = React.forwardRef<HTMLDivElement, UITaskProps>(
  ({
    title,
    description,
    steps,
    currentStep,
    overallProgress,
    variant = 'default',
    showProgress = true,
    showDescription = true,
    showStepNumber = true,
    className,
    ariaLabel,
    locale = 'ko',
    onStepClick,
    ...props
  }, ref) => {
    const completedSteps = steps.filter(step => step.status === 'completed').length
    const totalSteps = steps.length
    const calculatedProgress = overallProgress ?? (completedSteps / totalSteps) * 100

    const statusConfig = {
      pending: {
        icon: Circle,
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-amber-50/30 dark:bg-amber-950/20',
        shadowColor: 'shadow-yellow-500/[0.15] dark:shadow-yellow-400/[0.35]'
      },
      running: {
        icon: Loader2,
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50/30 dark:bg-blue-950/20',
        shadowColor: 'shadow-blue-500/[0.15] dark:shadow-blue-400/[0.35]'
      },
      completed: {
        icon: CheckCircle2,
        color: 'text-green-800 dark:text-green-400',
        bgColor: 'bg-green-50/30 dark:bg-green-950/20',
        shadowColor: 'shadow-green-500/[0.15] dark:shadow-green-400/[0.35]'
      },
      error: {
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50/30 dark:bg-red-950/20',
        shadowColor: 'shadow-red-500/[0.15] dark:shadow-red-400/[0.35]'
      },
      skipped: {
        icon: AlertCircle,
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-50/30 dark:bg-orange-950/20',
        shadowColor: 'shadow-orange-500/[0.15] dark:shadow-orange-400/[0.35]'
      }
    }

    if (variant === 'minimal') {
      return (
        <UIBox
          ref={ref}
          role="region"
          className={cn("space-y-2", className)}
          aria-label={ariaLabel || title}
          {...props}
        >
          <UIBox className="flex items-center justify-between">
            <UITypography variant="body2" weight="medium">
              {title}
            </UITypography>
            <UITypography variant="caption" className="text-muted-foreground">
              {completedSteps}/{totalSteps}
            </UITypography>
          </UIBox>
          {showProgress && (
            <UIProgress
              value={calculatedProgress}
              className="h-1"
              aria-label={locale === 'ko' ? '전체 작업 진행률' : 'Overall task progress'}
            />
          )}
        </UIBox>
      )
    }

    return (
      <UIBox
        ref={ref}
        role="region"
        className={cn(
          // Apple glassmorphism base
          "rounded-xl transition-all duration-300 ease-out border p-4 space-y-4",
          "bg-gradient-to-br from-white/[0.04] to-slate-50/[0.03] dark:from-white/[0.02] dark:to-slate-950/[0.015]",
          "border-white/[0.08] dark:border-white/[0.06]",
          "shadow-xl shadow-black/[0.02] dark:shadow-black/[0.08]",
          // Enhanced hover effects with lift
          "hover:-translate-y-0.5 hover:scale-[1.002] hover:shadow-2xl hover:shadow-black/[0.04] dark:hover:shadow-black/[0.15]",
          className
        )}
        aria-label={ariaLabel || title}
        {...props}
      >
        {/* 헤더 */}
        <UIBox>
          <UIBox className="flex items-center justify-between mb-1">
            <UITypography
              variant="h6"
              weight="semibold"
              className="drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] leading-relaxed"
            >
              {title}
            </UITypography>
            <UIBadge variant="secondary" size="sm">
              {completedSteps}/{totalSteps} {locale === 'ko' ? '완료' : 'completed'}
            </UIBadge>
          </UIBox>
          {showDescription && description && (
            <UITypography
              variant="body2"
              className="text-muted-foreground drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] leading-relaxed"
            >
              {description}
            </UITypography>
          )}
        </UIBox>

        {/* 전체 진행 바 */}
        {showProgress && (
          <UIBox>
            <UIBox className="flex items-center justify-between mb-2">
              <UITypography
                variant="caption"
                className="text-muted-foreground drop-shadow-sm leading-relaxed"
              >
                {locale === 'ko' ? '전체 진행률' : 'Overall Progress'}
              </UITypography>
              <UITypography
                variant="caption"
                weight="medium"
                className="drop-shadow-sm leading-relaxed"
              >
                {Math.round(calculatedProgress)}%
              </UITypography>
            </UIBox>
            <UIProgress
              value={calculatedProgress}
              aria-label={locale === 'ko' ? '전체 작업 진행률' : 'Overall task progress'}
            />
          </UIBox>
        )}

        {/* 단계 목록 */}
        <UIBox className="space-y-2">
          {steps.map((step, index) => {
            const config = statusConfig[step.status]
            const StatusIcon = config.icon
            const isCurrentStep = currentStep === step.id ||
                                (step.status === 'running' && !currentStep)

            return (
              <UIBox
                key={step.id}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg transition-all duration-300 ease-out",
                  "bg-white/[0.01] dark:bg-white/[0.005] border border-white/[0.04] dark:border-white/[0.03]",
                  isCurrentStep && [
                    config.bgColor,
                    config.shadowColor,
                    "border-white/[0.08] dark:border-white/[0.06]"
                  ],
                  onStepClick && [
                    "cursor-pointer group",
                    "hover:bg-white/[0.02] dark:hover:bg-white/[0.01]",
                    "hover:border-white/[0.08] dark:hover:border-white/[0.06]",
                    "hover:-translate-y-0.5 hover:scale-[1.002] hover:shadow-sm"
                  ]
                )}
                onClick={() => onStepClick?.(step.id)}
              >
                {/* 단계 번호 */}
                {showStepNumber && (
                  <UITypography
                    variant="caption"
                    className={cn(
                      "w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 ease-out",
                      "border border-white/[0.08] dark:border-white/[0.06]",
                      "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                      config.bgColor,
                      config.color,
                      config.shadowColor,
                      "font-medium"
                    )}
                  >
                    {index + 1}
                  </UITypography>
                )}

                {/* 상태 아이콘 */}
                <StatusIcon className={cn(
                  "h-5 w-5 mt-0.5 transition-all duration-300 ease-out",
                  "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                  config.color,
                  step.status === 'running' && "animate-spin",
                  onStepClick && "group-hover:scale-110"
                )} />

                {/* 단계 정보 */}
                <UIBox className="flex-1 min-w-0">
                  <UITypography
                    variant="body2"
                    weight={isCurrentStep ? "medium" : "normal"}
                    className={cn(
                      "transition-all duration-300 ease-out leading-relaxed",
                      "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]",
                      step.status === 'completed' && "line-through opacity-70",
                      step.status === 'error' && "text-destructive",
                      onStepClick && "group-hover:text-foreground/95"
                    )}
                  >
                    {step.title}
                  </UITypography>

                  {showDescription && step.description && (
                    <UITypography
                      variant="caption"
                      className="text-muted-foreground drop-shadow-sm leading-relaxed transition-colors duration-300 ease-out"
                    >
                      {step.description}
                    </UITypography>
                  )}

                  {step.progress !== undefined && step.status === 'running' && (
                    <UIBox className="mt-1">
                      <UIProgress
                        value={step.progress}
                        className="h-1"
                        aria-label={locale === 'ko' ? `${step.title} 진행률` : `${step.title} progress`}
                      />
                    </UIBox>
                  )}
                </UIBox>

                {/* 상태 라벨 */}
                {step.status !== 'pending' && (
                  <UITypography
                    variant="caption"
                    className={cn(
                      config.color,
                      "font-medium drop-shadow-sm leading-relaxed transition-all duration-300 ease-out"
                    )}
                  >
                    {step.status === 'running' && (locale === 'ko' ? '진행 중' : 'Running')}
                    {step.status === 'completed' && (locale === 'ko' ? '완료' : 'Done')}
                    {step.status === 'error' && (locale === 'ko' ? '오류' : 'Error')}
                    {step.status === 'skipped' && (locale === 'ko' ? '건너뜀' : 'Skipped')}
                  </UITypography>
                )}
              </UIBox>
            )
          })}
        </UIBox>
      </UIBox>
    )
  }
)

UITask.displayName = "UITask"